import { t } from '@lingui/macro';
import { first, isNil, last } from 'lodash';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AfterRenderQuestionEvent, Model, Question } from 'survey-core';
import 'survey-core/defaultV2.min.css';
import { Survey } from 'survey-react-ui';
import { useWorkflowNavigation } from '../../../shared/view/components/Workflow';
import WithingsScale from '../../../shared/view/icons/images/withings-body-pro-2.png';
import { BackHeader } from '../../Onboarding/shared/view/BackHeader';
import { ScreenContentContainer } from '../../Onboarding/shared/view/ScreenContentContainer';
import { surveyStyle } from '../../Onboarding/shared/view/SurveyPage/survey.style';
import { useCheckInCustomizationContext } from '../customization/CheckInCustomizationContext';
import { checkInCustomPages } from '../customization/registry';
import {
  WITHINGS_PREFILL_PROPERTY,
  WITHINGS_PREFILL_SOURCE,
  isWeighingReminder,
} from '../customization/withingsPrefill/withingsPrefill.config';
import './checkin.css';

type CheckInSurveyPageProps = {
  setSurveyObject: (data: Record<string, string>) => void;
  model: Model;
  surveyLabel: string;
  defaultData?: Record<string, string>;
};

const isPageInModel = (model: Model, pageName: string | undefined): boolean => {
  if (isNil(pageName)) return false;
  return !isNil(model.getPageByName(pageName));
};

const isWithingsQuestion = (question: Question): boolean =>
  question.getPropertyValue(WITHINGS_PREFILL_PROPERTY) ===
  WITHINGS_PREFILL_SOURCE;

const insertWithingsNotice = (htmlElement: HTMLElement, label: string) => {
  if (htmlElement.querySelector('.withings-prefill-notice')) return;
  const notice = document.createElement('div');
  notice.className = 'withings-prefill-notice';
  const icon = document.createElement('span');
  icon.className = 'withings-prefill-notice__icon';
  icon.style.backgroundImage = `url("${WithingsScale}")`;
  icon.setAttribute('aria-hidden', 'true');
  const text = document.createElement('span');
  text.textContent = label;
  notice.append(icon, text);
  const host =
    htmlElement.querySelector('.sd-question__content') ?? htmlElement;
  host.appendChild(notice);
};

const removeWeighingReminder = (htmlElement: HTMLElement) => {
  htmlElement
    .querySelectorAll('.sd-question__description')
    .forEach((description) => {
      if (isWeighingReminder(description.textContent)) description.remove();
    });
};

const decorateWithingsQuestion = (
  question: Question,
  htmlElement: HTMLElement,
) => {
  if (!isWithingsQuestion(question)) return;
  removeWeighingReminder(htmlElement);
  if (
    (question.value == null || question.value === '') &&
    question.defaultValue != null &&
    question.defaultValue !== ''
  ) {
    question.value = question.defaultValue;
  }
  insertWithingsNotice(htmlElement, t`checkin.withings.prefilled`);
};

const answersWithQuestionDefaults = (
  model: Model,
  answers: Record<string, string>,
): Record<string, string> => {
  const merged = { ...answers };
  model.getAllQuestions().forEach((question) => {
    const current = merged[question.name];
    const fallback = question.defaultValue;
    if (
      (current == null || current === '') &&
      fallback != null &&
      fallback !== ''
    ) {
      merged[question.name] = String(fallback);
    }
  });
  return merged;
};

const CheckInSurveyPage = (props: CheckInSurveyPageProps) => {
  const { model, setSurveyObject, defaultData = {} } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const customizationContext = useCheckInCustomizationContext();

  const pageNameFromUrl = useCallback(
    () => last(location.pathname.split('/')),
    [location.pathname],
  );

  const pageFromURL = pageNameFromUrl();
  const initialPageToLoad = isPageInModel(model, pageFromURL)
    ? pageFromURL
    : first(model.pages)?.name;

  const [pageName, setPageName] = useState(initialPageToLoad);
  const workflowNavigation = useWorkflowNavigation().navigation;

  useEffect(() => {
    setPageName(pageNameFromUrl());
  }, [location.pathname, pageNameFromUrl]);

  useEffect(() => {
    if (!isNil(pageName) && pageNameFromUrl() !== pageName) {
      const replace = !isPageInModel(model, pageNameFromUrl());
      navigate(pageName, { replace });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageName, model]);

  const onAfterRenderPage = useCallback((sender: Model) => {
    setPageName(sender.currentPage.name);
  }, []);

  const onValueChanged = useCallback(
    (survey: Model) => {
      setSurveyObject(survey.data);
    },
    [setSurveyObject],
  );

  const onAfterRenderQuestion = useCallback(
    (_sender: Model, options: AfterRenderQuestionEvent) => {
      decorateWithingsQuestion(options.question, options.htmlElement);
    },
    [],
  );

  const onCompleting = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (survey: Model, options: any) => {
      setSurveyObject(survey.data);
      options.allow = false;
      workflowNavigation.goNext();
    },
    [setSurveyObject, workflowNavigation],
  );

  // Custom pages drive navigation through model.nextPage(); we still want the
  // current page name to follow along. Listen to the model directly so the URL
  // stays in sync even when SurveyJS doesn't render anything visible.
  const hasAppliedDefaults = useRef(false);

  useEffect(() => {
    hasAppliedDefaults.current = false;
  }, [model]);

  useEffect(() => {
    const onPageChanged = () => setPageName(model.currentPage?.name);
    model.onAfterRenderPage.clear();
    model.onAfterRenderPage.add(onAfterRenderPage);
    model.onAfterRenderQuestion.clear();
    model.onAfterRenderQuestion.add(onAfterRenderQuestion);
    model.onCurrentPageChanged.clear();
    model.onCurrentPageChanged.add(onPageChanged);
    model.onValueChanged.clear();
    model.onValueChanged.add(onValueChanged);
    model.onCompleting.clear();
    model.onCompleting.add(onCompleting);
    // Apply Withings defaults once, and only where the patient has not typed anything yet.
    if (!hasAppliedDefaults.current) {
      model.data = answersWithQuestionDefaults(model, defaultData);
      hasAppliedDefaults.current = true;
    }
  }, [
    model,
    defaultData,
    onAfterRenderPage,
    onAfterRenderQuestion,
    onValueChanged,
    onCompleting,
  ]);

  useLayoutEffect(() => {
    document
      .querySelectorAll('.sd-question__description--under-input > span')
      .forEach((spanElement) => {
        if (
          spanElement.innerHTML === '' &&
          spanElement.parentElement !== null
        ) {
          spanElement.parentElement.style.display = 'none';
        }
      });
    // Questions mounted before onAfterRenderQuestion was attached never fire it.
    model.getAllQuestions().forEach((question) => {
      const htmlElement = document.querySelector<HTMLElement>(
        `.sd-question[data-name="${question.name}"]`,
      );
      if (htmlElement) decorateWithingsQuestion(question, htmlElement);
    });
  }, [model, pageName]);

  const CustomPage = pageName ? checkInCustomPages[pageName] : undefined;

  return (
    <ScreenContentContainer>
      <div className="flex flex-col flex-grow w-full">
        <BackHeader />
        {CustomPage ? (
          <CustomPage model={model} context={customizationContext} />
        ) : (
          <Survey
            currentPage={pageName}
            model={model}
            showCompletePage={false}
            css={surveyStyle}
            goNextPageAutomatic={false}
            autoAdvanceDelay={0}
          />
        )}
      </div>
    </ScreenContentContainer>
  );
};

export default CheckInSurveyPage;
